/**
 * Lucide Icon Picker Component
 * 
 * A grid-based picker for selecting Lucide icons for navigation items
 * with continuous scrolling and auto-updating category tabs
 */

import React, { useState, useRef, useCallback } from "react";
import { icons as lucideIconsMap } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import {
  MessageSquare,
  MessageCircle,
  Keyboard,
  Moon,
  Music,
  HeartHandshake,
  Youtube,
  Tv,
  Radio,
  Puzzle,
  Home,
  Settings,
  Bell,
  Phone,
  Mail,
  Camera,
  Video,
  Mic,
  Volume2,
  Play,
  Pause,
  Heart,
  Star,
  Sun,
  Cloud,
  Zap,
  Coffee,
  Book,
  Bookmark,
  Calendar,
  Clock,
  AlertCircle,
  HelpCircle,
  Info,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  User,
  Users,
  Search,
  Filter,
  Menu,
  MoreHorizontal,
  Plus,
  Minus,
  X,
  Check,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  RotateCcw,
  Download,
  Upload,
  Share,
  Copy,
  Clipboard,
  Trash2,
  Edit,
  Save,
  Folder,
  File,
  FileText,
  Image,
  Film,
  Headphones,
  Speaker,
  Wifi,
  Bluetooth,
  Battery,
  Power,
  Thermometer,
  Droplet,
  Wind,
  Umbrella,
  MapPin,
  Navigation,
  Compass,
  Globe,
  Flag,
  Award,
  Gift,
  ShoppingCart,
  CreditCard,
  Wallet,
  Briefcase,
  Truck,
  Car,
  Plane,
  Train,
  Ship,
  Bike,
  Footprints,
  Accessibility,
  Hand,
  Pointer,
  MousePointer,
  Move,
  Maximize,
  Minimize,
  LayoutGrid,
  List,
  Grid,
  Table,
  PieChart,
  BarChart,
  TrendingUp,
  Activity,
  Target,
  Crosshair,
  Smile,
  Frown,
  Meh,
  Angry,
  Laugh,
  type LucideIcon,
} from "lucide-react";

// Icon categories with names
const ICON_CATEGORIES = {
  navigation: {
    icons: ["Home", "Menu", "Search", "Settings", "HelpCircle", "Info", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "ChevronUp", "ChevronDown", "Navigation", "Compass", "MapPin"],
    label: { he: "ניווט", en: "Navigation" },
  },
  communication: {
    icons: ["MessageSquare", "MessageCircle", "Phone", "Mail", "Video", "Mic", "Volume2", "Bell", "Keyboard", "Share"],
    label: { he: "תקשורת", en: "Communication" },
  },
  media: {
    icons: ["Youtube", "Music", "Tv", "Radio", "Puzzle", "Play", "Pause", "Headphones", "Speaker", "Camera", "Film", "Image"],
    label: { he: "מדיה", en: "Media" },
  },
  actions: {
    icons: ["Plus", "Minus", "Check", "X", "Edit", "Save", "Copy", "Trash2", "Download", "Upload", "RefreshCw", "RotateCcw", "Move"],
    label: { he: "פעולות", en: "Actions" },
  },
  status: {
    icons: ["CheckCircle", "XCircle", "AlertCircle", "Eye", "EyeOff", "Lock", "Unlock", "Power", "Battery", "Wifi", "Bluetooth"],
    label: { he: "סטטוס", en: "Status" },
  },
  people: {
    icons: ["User", "Users", "Hand", "Pointer", "HeartHandshake", "Accessibility", "Footprints"],
    label: { he: "אנשים", en: "People" },
  },
  feelings: {
    icons: ["Heart", "Star", "Smile", "Frown", "Meh", "Angry", "Laugh", "Moon", "Sun", "Zap"],
    label: { he: "רגשות", en: "Feelings" },
  },
  objects: {
    icons: ["Book", "Bookmark", "Calendar", "Clock", "Coffee", "Gift", "Briefcase", "Wallet", "CreditCard", "ShoppingCart", "Folder", "File", "FileText"],
    label: { he: "חפצים", en: "Objects" },
  },
  nature: {
    icons: ["Cloud", "Droplet", "Wind", "Umbrella", "Thermometer", "Globe", "Flag"],
    label: { he: "טבע", en: "Nature" },
  },
  transport: {
    icons: ["Car", "Truck", "Plane", "Train", "Ship", "Bike"],
    label: { he: "תחבורה", en: "Transport" },
  },
};

type CategoryKey = keyof typeof ICON_CATEGORIES;

// Map icon names to components
const ICON_MAP: Record<string, LucideIcon> = {
  MessageSquare, MessageCircle, Keyboard, Moon, Music, HeartHandshake, Youtube, Tv, Radio, Puzzle,
  Home, Settings, Bell, Phone, Mail, Camera, Video, Mic, Volume2, Play, Pause,
  Heart, Star, Sun, Cloud, Zap, Coffee, Book, Bookmark, Calendar, Clock,
  AlertCircle, HelpCircle, Info, CheckCircle, XCircle, Eye, EyeOff, Lock, Unlock,
  User, Users, Search, Filter, Menu, MoreHorizontal, Plus, Minus, X, Check,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  RefreshCw, RotateCcw, Download, Upload, Share, Copy, Clipboard, Trash2, Edit, Save,
  Folder, File, FileText, Image, Film, Headphones, Speaker, Wifi, Bluetooth, Battery, Power,
  Thermometer, Droplet, Wind, Umbrella, MapPin, Navigation, Compass, Globe, Flag, Award, Gift,
  ShoppingCart, CreditCard, Wallet, Briefcase, Truck, Car, Plane, Train, Ship, Bike, Footprints,
  Accessibility, Hand, Pointer, MousePointer, Move, Maximize, Minimize, LayoutGrid, List, Grid,
  Table, PieChart, BarChart, TrendingUp, Activity, Target, Crosshair, Smile, Frown, Meh, Angry, Laugh,
};

interface LucideIconPickerProps {
  selectedIcon: string;
  onIconSelect: (iconName: string) => void;
  isHebrew?: boolean;
}

export function LucideIconPicker({ selectedIcon, onIconSelect, isHebrew = false }: LucideIconPickerProps) {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("navigation");
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  const SelectedIconComponent = ICON_MAP[selectedIcon] || (lucideIconsMap as any)[selectedIcon] || Home;
  const allCategories = Object.keys(ICON_CATEGORIES) as CategoryKey[];

  const scrollToCategory = (category: CategoryKey) => {
    const element = categoryRefs.current[category];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Track scroll position to update active category
  const handleScroll = useCallback(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    const scrollTop = viewport.scrollTop;
    
    let currentCategory: CategoryKey = allCategories[0];
    
    for (const category of allCategories) {
      const element = categoryRefs.current[category];
      if (element) {
        const offsetTop = element.offsetTop - viewport.offsetTop;
        if (scrollTop >= offsetTop - 50) {
          currentCategory = category;
        }
      }
    }
    
    setActiveCategory(currentCategory);
  }, [allCategories]);

  return (
    <div className="space-y-4">
      {/* Selected icon display */}
      <div className="flex items-center justify-center gap-4 p-4 bg-muted rounded-xl">
        <SelectedIconComponent className="w-16 h-16 text-foreground" />
      </div>

      {/* Category quick-jump tabs with active indicator */}
      <ScrollArea className="w-full whitespace-nowrap" dir={isHebrew ? "rtl" : "ltr"}>
        <div className="flex gap-2 pb-3 px-1">
          {allCategories.map((category) => (
            <Button
              key={category}
              variant={activeCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => scrollToCategory(category)}
              className={cn(
                "whitespace-nowrap shrink-0 transition-all",
                activeCategory === category && "ring-2 ring-primary/50"
              )}
            >
              {isHebrew 
                ? ICON_CATEGORIES[category].label.he 
                : ICON_CATEGORIES[category].label.en}
            </Button>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* All icons in one scrollable area - continuous scroll */}
      <ScrollAreaPrimitive.Root className="relative overflow-hidden h-64 rounded-xl bg-muted/50">
        <ScrollAreaPrimitive.Viewport 
          ref={scrollViewportRef}
          className="h-full w-full rounded-[inherit]"
          onScroll={handleScroll}
        >
          <div className="p-3 space-y-4">
            {allCategories.map((category) => (
              <div 
                key={category}
                ref={(el) => { categoryRefs.current[category] = el; }}
              >
                {/* Icon grid for this category */}
                <div className="grid grid-cols-5 gap-2">
                  {ICON_CATEGORIES[category].icons.map((iconName, index) => {
                    const IconComponent = ICON_MAP[iconName];
                    if (!IconComponent) return null;
                    
                    return (
                      <button
                        key={`${category}-${iconName}-${index}`}
                        onClick={() => onIconSelect(iconName)}
                        className={cn(
                          "p-3 rounded-lg transition-all hover:bg-primary/20 hover:scale-110 flex items-center justify-center",
                          selectedIcon === iconName && "bg-primary/30 ring-2 ring-primary"
                        )}
                      >
                        <IconComponent className="w-8 h-8 text-foreground" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    </div>
  );
}

// Export the icon map for use in rendering
export { ICON_MAP };
